import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError, customInstance } from "@repo/api-client";
import { venueConfigSchema, type VenueConfig } from "./types";

/**
 * Anonymous fetch of the public venue booking config through the ADR-0020 fetch mutator
 * (`customInstance`) — no token provider is ever set in this app, so the call is uncredentialed
 * (spec: no `Authorization`, no cookies).
 *
 * The endpoint is the slug-keyed anonymous discovery read on the CANONICAL public prefix
 * `/api/public/v1/venues/by-slug/{slug}/booking-config` (visual-qa-bug-fixes P1 landed it beside the
 * venueId-keyed `/api/public/v1/venues/{venueId}/…` family; the widget previously called a phantom
 * `/api/v1/public/…` path that only the MSW stub answered, so every real venue was "not found").
 *
 * Error mapping (P4 — a link is only "not valid" when the slug is genuinely unknown):
 *  - ONLY a `404` (unknown/unpublished slug) is surfaced as `null` → the not-found shell;
 *  - a resolved-but-not-live venue is a 200 with `status: "not_live" | "online_booking_disabled"`
 *    (the shell renders the "unavailable" frame, never not-found);
 *  - EVERY other failure (401/403/422/5xx, network down, malformed body) THROWS → the ERROR state.
 *    A backend outage must never masquerade as "this booking link is not valid".
 */
export async function fetchVenueConfig(slug: string): Promise<VenueConfig | null> {
  try {
    const body = await customInstance<unknown>(
      `/api/public/v1/venues/by-slug/${encodeURIComponent(slug)}/booking-config`,
      { method: "GET" },
    );
    return venueConfigSchema.parse(body);
  } catch (error) {
    // 404 ALONE means "no such venue" → not-found. Anything else (incl. other ApiError statuses,
    // TypeErrors from a dead backend, ZodErrors from a malformed body) propagates → error state.
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/** The five mutually-exclusive shell states derived from the config query (see `deriveShellState`). */
export type ShellState = "loading" | "error" | "not-found" | "unavailable" | "ready";

/**
 * Pure mapping from a config query result to a shell state — the single place the fail-safe policy
 * lives, so it is unit-testable without React. `online_booking_disabled`/`not_live` render the
 * "booking currently unavailable" frame (DOMAIN.md §C.6), never an error or a blank.
 */
export function deriveShellState(input: {
  isLoading: boolean;
  isError: boolean;
  data: VenueConfig | null | undefined;
}): ShellState {
  if (input.isLoading) return "loading";
  if (input.isError) return "error";
  if (input.data === null || input.data === undefined) return "not-found";
  return input.data.status === "live" ? "ready" : "unavailable";
}

export function useVenueConfig(slug: string): UseQueryResult<VenueConfig | null> {
  return useQuery({
    queryKey: ["venue-config", slug],
    queryFn: () => fetchVenueConfig(slug),
    retry: 1,
    staleTime: 60_000,
  });
}
